import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { metaCompraApi } from '../api/metaCompra';
import type {
  AtualizarMetaCompraRequest,
  CriarMetaCompraRequest,
  DimensaoDrilldown,
} from '../types/metaCompra';

const KEY_LISTA = 'metaCompra-lista';
const KEY_DASHBOARD = 'metaCompra-dashboard';
const KEY_HISTORICO = 'metaCompra-historico';
const KEY_DRILLDOWN = 'metaCompra-drilldown';

const STALE_DASHBOARD = 30 * 1000; // 30s — atualiza ao criar pedido novo

export function useMetasCompra(filtros: { anoMes?: string; empresa?: string }) {
  return useQuery({
    queryKey: [KEY_LISTA, filtros],
    queryFn: () => metaCompraApi.listar(filtros),
    staleTime: STALE_DASHBOARD,
  });
}

export function useDashboardMetaCompra(anoMes: string, empresas: string[], enabled = true) {
  return useQuery({
    queryKey: [KEY_DASHBOARD, anoMes, [...empresas].sort()],
    queryFn: () => metaCompraApi.dashboard(anoMes, empresas),
    enabled: enabled && !!anoMes && empresas.length > 0,
    staleTime: STALE_DASHBOARD,
  });
}

/**
 * Histórico mensal compacto (default 6 meses) pra sparkline + comparativo MoM
 * na aba Metas da Reposição. Só dispara quando empresas selecionadas.
 */
export function useHistoricoMetaCompra(
  anoMes: string,
  empresas: string[],
  meses = 6,
  enabled = true,
) {
  return useQuery({
    queryKey: [KEY_HISTORICO, anoMes, [...empresas].sort(), meses],
    queryFn: () => metaCompraApi.dashboardHistorico(anoMes, empresas, meses),
    enabled: enabled && !!anoMes && empresas.length > 0,
    staleTime: STALE_DASHBOARD,
  });
}

export function useDrilldownMetaCompra(
  anoMes: string,
  empresas: string[],
  dimensao: DimensaoDrilldown | null,
  codigo: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: [KEY_DRILLDOWN, anoMes, [...empresas].sort(), dimensao, codigo],
    queryFn: () => metaCompraApi.drilldown(anoMes, empresas, dimensao!, codigo!),
    enabled: enabled && !!anoMes && empresas.length > 0 && !!dimensao && !!codigo,
    staleTime: STALE_DASHBOARD,
  });
}

export function useCriarMetaCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (req: CriarMetaCompraRequest) => metaCompraApi.criar(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY_LISTA] });
      qc.invalidateQueries({ queryKey: [KEY_DASHBOARD] });
    },
  });
}

export function useAtualizarMetaCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: number; req: AtualizarMetaCompraRequest }) =>
      metaCompraApi.atualizar(args.id, args.req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY_LISTA] });
      qc.invalidateQueries({ queryKey: [KEY_DASHBOARD] });
    },
  });
}

export function useExcluirMetaCompra() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => metaCompraApi.excluir(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY_LISTA] });
      qc.invalidateQueries({ queryKey: [KEY_DASHBOARD] });
    },
  });
}
